import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, ImagePlus, Instagram } from "lucide-react";
import { API, authHeaders, imageSrc, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const EMPTY = { image: "", caption: "", link: "https://instagram.com/discohotdogs" };

export default function GalleryTab() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () =>
    axios.get(`${API}/gallery`).then((res) => setItems(res.data)).catch(() => toast.error("Could not load gallery"));

  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await axios.post(`${API}/upload`, data, authHeaders());
      setForm((f) => ({ ...f, image: res.data.path }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.image) {
      toast.error("Upload a photo first");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/gallery`, form, authHeaders());
      toast.success("Added to the gallery strip");
      setDialogOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Remove this photo from the gallery strip?")) return;
    try {
      await axios.delete(`${API}/gallery/${item.id}`, authHeaders());
      toast.success("Photo removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-gallery-tab">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <p className="text-sm text-zinc-400 max-w-lg">
          These photos fill the "@discohotdogs" strip on the site. When you post to Instagram, upload the same shot here - newest first.
        </p>
        <button
          data-testid="gallery-add-button"
          onClick={() => setDialogOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-disco-yellow text-disco-void text-xs font-bold px-5 py-2.5 transition-all duration-300 hover:shadow-[0_0_20px_rgba(226,255,0,0.35)] active:scale-95"
        >
          <Plus size={14} /> Add Photo
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-white/10 py-16 text-center text-zinc-500 text-sm" data-testid="gallery-empty-state">
          No photos yet. Add one and the strip appears on the site.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item) => (
            <div key={item.id} data-testid={`admin-gallery-item-${item.id}`} className="group relative aspect-square overflow-hidden border border-white/10">
              <img src={imageSrc(item.image)} alt={item.caption || "Gallery photo"} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-300" />
              <button
                data-testid={`gallery-delete-${item.id}`}
                onClick={() => remove(item)}
                className="absolute top-2 right-2 p-2 bg-black/60 text-zinc-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                aria-label="Remove photo"
              >
                <Trash2 size={14} />
              </button>
              {item.caption && (
                <p className="absolute bottom-0 inset-x-0 p-2 text-[11px] text-zinc-200 bg-black/60 truncate">{item.caption}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="gallery-dialog" className="bg-disco-surface border-white/10 text-zinc-100 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-wide text-2xl flex items-center gap-2">
              <Instagram size={20} className="text-disco-lavender" /> New Gallery Photo
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Photo</Label>
              <div className="flex items-center gap-4">
                {form.image && (
                  <img src={imageSrc(form.image)} alt="Gallery preview" className="w-16 h-16 object-cover border border-white/10" />
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="gallery-upload-input" />
                <button
                  type="button"
                  data-testid="gallery-upload-button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 rounded-full border border-disco-teal/50 text-disco-teal text-xs font-semibold px-4 py-2 transition-colors duration-300 hover:bg-disco-teal/10 active:scale-95 disabled:opacity-50"
                >
                  <ImagePlus size={14} />
                  {uploading ? "Uploading..." : form.image ? "Change Photo" : "Upload Photo"}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Caption (optional)</Label>
              <Input
                data-testid="gallery-form-caption"
                value={form.caption}
                onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))}
                placeholder="Neon Loaded, fresh off the grill"
                className="bg-white/5 border-white/10 text-zinc-100"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Link (optional)</Label>
              <Input
                data-testid="gallery-form-link"
                value={form.link}
                onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100"
              />
              <p className="text-[11px] text-zinc-500">Paste the Instagram post's link, or leave the profile link.</p>
            </div>
            <button
              data-testid="gallery-save-button"
              type="submit"
              disabled={saving || uploading}
              className="w-full rounded-full bg-disco-yellow text-disco-void font-semibold py-3 text-sm transition-all duration-300 hover:shadow-[0_0_20px_rgba(226,255,0,0.35)] active:scale-[0.98] disabled:opacity-50"
            >
              {saving ? "Adding..." : "Add to Gallery"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
